package com.patronat.top.features.venues.data.network

import com.patronat.top.core.network.NetworkResult
import com.patronat.top.core.network.safeApiCall
import com.patronat.top.dto.CreateVenueRequest
import com.patronat.top.dto.PatchVenueRequest
import com.patronat.top.dto.ReassignVenueModeratorRequest
import com.patronat.top.dto.ReviewVenueDraftRequest
import com.patronat.top.dto.UpdateVenueRequest
import com.patronat.top.dto.UpdateVenueStatusRequest
import com.patronat.top.dto.VenueAssigneesResponse
import com.patronat.top.dto.VenueAiImportRequest
import com.patronat.top.dto.VenueAiImportResponse
import com.patronat.top.dto.VenueDraftResponse
import com.patronat.top.dto.VenueFilterRequest
import com.patronat.top.dto.VenueListResponse
import com.patronat.top.dto.VenueResponse
import com.patronat.top.dto.venues.VenueStatus
import io.ktor.client.HttpClient
import io.ktor.client.plugins.timeout
import io.ktor.client.request.get
import io.ktor.client.request.patch
import io.ktor.client.request.post
import io.ktor.client.request.put
import io.ktor.client.request.setBody

class VenuesRemoteDataSource(
    private val httpClient: HttpClient
) {

    suspend fun createVenue(request: CreateVenueRequest): NetworkResult<VenueResponse> {
        return safeApiCall {
            httpClient.post("venues/create") {
                setBody(request)
            }
        }
    }

    suspend fun importVenueWithAi(request: VenueAiImportRequest): NetworkResult<VenueAiImportResponse> {
        return safeApiCall {
            httpClient.post("venues/ai-import") {
                timeout {
                    requestTimeoutMillis = AiImportRequestTimeoutMillis
                    socketTimeoutMillis = AiImportRequestTimeoutMillis
                    connectTimeoutMillis = 30_000
                }
                setBody(request)
            }
        }
    }

    suspend fun getVenueById(venueId: String): NetworkResult<VenueResponse> {
        return safeApiCall {
            httpClient.get("venues/$venueId")
        }
    }

    suspend fun getVenueDraft(venueId: String): NetworkResult<VenueDraftResponse> {
        return safeApiCall {
            httpClient.get("venues/$venueId/draft")
        }
    }

    suspend fun getSubmittedVenueDraft(venueId: String): NetworkResult<VenueDraftResponse> {
        return safeApiCall {
            httpClient.get("venues/$venueId/draft/submitted")
        }
    }

    suspend fun updateVenue(venueId: String, request: UpdateVenueRequest): NetworkResult<VenueResponse> {
        return safeApiCall {
            httpClient.put("venues/$venueId") {
                setBody(request)
            }
        }
    }

    suspend fun patchVenueDraft(venueId: String, request: PatchVenueRequest): NetworkResult<VenueDraftResponse> {
        return safeApiCall {
            httpClient.patch("venues/$venueId/draft") {
                setBody(request)
            }
        }
    }

    suspend fun reviewVenueDraft(venueId: String, approved: Boolean): NetworkResult<VenueDraftResponse> {
        return safeApiCall {
            httpClient.put("venues/$venueId/draft/review") {
                setBody(ReviewVenueDraftRequest(approved = approved))
            }
        }
    }

    suspend fun filterVenues(request: VenueFilterRequest): NetworkResult<VenueListResponse> {
        return safeApiCall {
            httpClient.post("venues/filter") {
                setBody(request)
            }
        }
    }

    suspend fun getVenueAssignees(): NetworkResult<VenueAssigneesResponse> {
        return safeApiCall {
            httpClient.get("venues/assignees")
        }
    }

    suspend fun reassignVenueModerator(venueId: String, moderatorId: String): NetworkResult<VenueResponse> {
        return safeApiCall {
            httpClient.put("venues/$venueId/moderator") {
                setBody(ReassignVenueModeratorRequest(moderatorId = moderatorId))
            }
        }
    }

    suspend fun updateVenueStatus(venueId: String, status: VenueStatus): NetworkResult<VenueResponse> {
        return safeApiCall {
            httpClient.put("venues/$venueId/status") {
                setBody(UpdateVenueStatusRequest(status))
            }
        }
    }

    private companion object {
        const val AiImportRequestTimeoutMillis = 310_000L
    }
}
